import { useCallback, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Banknote,
  ClipboardPaste,
  Inbox,
  Loader2,
  Plus,
  Sparkles,
  Wallet,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Dialog } from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Field } from "@/components/ui/Label";
import { Skeleton } from "@/components/ui/Skeleton";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  useAccounts,
  useApplyClassification,
  useCreateAccount,
  useCreateInvoice,
  useCreateTransaction,
  useInvoices,
  useMarkInvoicePaid,
  useTransactionSuggestions,
  useTransactions,
  type AccountPurpose,
  type AccountType,
  type Invoice,
  type TransactionDirection,
} from "@/hooks/useMoney";
import {
  useIngestPaystub,
  useParsePaystub,
  type ParsedPaystub,
} from "@/hooks/usePaystubs";
import { useStrategyAssumptions } from "@/hooks/useStrategy";
import {
  useBillingProjects,
  useProjectTasks,
  useCreateBillingProject,
  useCreateProjectTask,
} from "@/hooks/useBillingProjects";
import { formatDate, formatMoney, todayIso } from "@/lib/utils";

export function MoneyPage() {
  const accounts = useAccounts();
  const invoices = useInvoices();
  const transactions = useTransactions();
  const assumptions = useStrategyAssumptions();
  const [accountOpen, setAccountOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [txnOpen, setTxnOpen] = useState(false);
  const [payOpen, setPayOpen] = useState<Invoice | null>(null);
  const [paystubOpen, setPaystubOpen] = useState(false);

  const allInvoices = invoices.data ?? [];
  const outstanding = allInvoices
    .filter((i) => i.status !== "Paid" && i.status !== "Voided")
    .reduce((s, i) => s + (i.total - i.amountPaid), 0);
  const paidYtd = allInvoices.reduce((s, i) => s + i.amountPaid, 0);
  const expensesYtd = (transactions.data ?? [])
    .filter((t) => t.direction === "Expense")
    .reduce((s, t) => s + t.amount, 0);

  const hourlyRate = assumptions.data?.hourlyRate ?? null;
  const hoursPerWeek = assumptions.data?.hoursPerWeek ?? null;
  const expectedMonthly =
    hourlyRate != null && hoursPerWeek != null ? hourlyRate * hoursPerWeek * 4 : null;

  return (
    <>
      <PageHeader
        eyebrow="Money"
        title="Invoices, transactions, and accounts"
        description="Track invoices, mark payments, and manage accounts."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setAccountOpen(true)}>
              <Wallet className="h-4 w-4" />
              Add account
            </Button>
            <Button variant="outline" onClick={() => setTxnOpen(true)}>
              <Plus className="h-4 w-4" />
              Add transaction
            </Button>
            <Button variant="outline" onClick={() => setPaystubOpen(true)}>
              <ClipboardPaste className="h-4 w-4" />
              Paste paystub
            </Button>
            <Button onClick={() => setInvoiceOpen(true)}>
              <Plus className="h-4 w-4" />
              New invoice
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Stat label="Outstanding" value={formatMoney(outstanding)} loading={invoices.isLoading} />
        <Stat label="Paid YTD" value={formatMoney(paidYtd)} loading={invoices.isLoading} />
        <Stat label="Expenses YTD" value={formatMoney(expensesYtd)} loading={transactions.isLoading} />
        <Stat
          label="Expected monthly"
          value={formatMoney(expectedMonthly)}
          hint={
            hourlyRate != null && hoursPerWeek != null
              ? `${hoursPerWeek} hrs/wk × $${hourlyRate}/hr × 4`
              : "Add rate & hours in Settings"
          }
          loading={assumptions.isLoading}
        />
      </div>


      <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Banknote className="h-4 w-4 text-primary" />
              Accounts
            </CardTitle>
            <CardDescription>Operating, tax savings, retirement.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {accounts.isLoading && <Skeleton className="h-20 w-full" />}
            {!accounts.isLoading && (accounts.data?.length ?? 0) === 0 && (
              <p className="text-muted-foreground">
                No accounts yet. Add your business checking + tax savings sub-account.
              </p>
            )}
            {(accounts.data ?? []).map((a) => (
              <div
                key={a.id}
                className="flex items-center justify-between rounded-md border border-border/60 p-3"
              >
                <div>
                  <div className="font-medium">{a.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {humanAccountType(a.accountType)} · {humanAccountPurpose(a.purpose)}
                    {a.last4 && ` · …${a.last4}`}
                  </div>
                </div>
                <div className="text-right text-xs text-muted-foreground">
                  Opened {formatDate(a.openingBalanceDate)}
                  <br />
                  {formatMoney(a.openingBalance)}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Invoices</CardTitle>
            <CardDescription>Draft → Sent → Paid; mark paid to auto-create a deposit.</CardDescription>
          </CardHeader>
          <CardContent>
            {invoices.isLoading && <Skeleton className="h-24 w-full" />}
            {!invoices.isLoading && allInvoices.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No invoices yet. Issue your first one to your client at the assumed rate.
              </p>
            )}
            {allInvoices.length > 0 && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="py-2">Number</th>
                    <th className="py-2">Issued</th>
                    <th className="py-2">Due</th>
                    <th className="py-2 text-right">Total</th>
                    <th className="py-2">Status</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {allInvoices.map((inv) => (
                    <tr key={inv.id} className="border-b border-border/50">
                      <td className="py-2 font-medium">{inv.number}</td>
                      <td className="py-2 text-muted-foreground">{formatDate(inv.issueDate)}</td>
                      <td className="py-2 text-muted-foreground">{formatDate(inv.dueDate)}</td>
                      <td className="py-2 text-right">{formatMoney(inv.total)}</td>
                      <td className="py-2">
                        <Badge tone={invoiceTone(inv.status)}>{inv.status}</Badge>
                      </td>
                      <td className="py-2 text-right">
                        {inv.status !== "Paid" && (
                          <Button size="sm" variant="outline" onClick={() => setPayOpen(inv)}>
                            Mark paid
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      <SuggestionsCard />

      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Recent transactions</CardTitle>
          <CardDescription>
            Across all accounts, newest first. Income vs expense vs tax — colour-coded.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {transactions.isLoading && <Skeleton className="h-24 w-full" />}
          {!transactions.isLoading && (transactions.data?.length ?? 0) === 0 && (
            <p className="text-sm text-muted-foreground">
              No transactions yet. Mark an invoice paid or add one manually.
            </p>
          )}
          {(transactions.data?.length ?? 0) > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="py-2">Date</th>
                  <th className="py-2">Description</th>
                  <th className="py-2">Account</th>
                  <th className="py-2">Direction</th>
                  <th className="py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(transactions.data ?? []).slice(0, 30).map((t) => {
                  const acct = accounts.data?.find((a) => a.id === t.financialAccountId);
                  const positive = t.direction === "Income" || t.direction === "OwnerContribution";
                  return (
                    <tr key={t.id} className="border-b border-border/50">
                      <td className="py-2 text-muted-foreground">{formatDate(t.date)}</td>
                      <td className="py-2">
                        <div className="font-medium">{t.description}</div>
                        {t.counterParty && (
                          <div className="text-xs text-muted-foreground">{t.counterParty}</div>
                        )}
                      </td>
                      <td className="py-2 text-muted-foreground">{acct?.name ?? "—"}</td>
                      <td className="py-2">
                        <span className="inline-flex items-center gap-1 text-xs">
                          {positive ? (
                            <ArrowDownRight className="h-3 w-3 text-emerald-400" />
                          ) : (
                            <ArrowUpRight className="h-3 w-3 text-rose-400" />
                          )}
                          {t.direction}
                        </span>
                      </td>
                      <td
                        className={`py-2 text-right font-medium ${positive ? "text-emerald-300" : "text-foreground"}`}
                      >
                        {positive ? "+" : "−"}
                        {formatMoney(t.amount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Inbox className="h-4 w-4 text-primary" />
            Paystub inbox
          </CardTitle>
          <CardDescription>
            Paste a paystub email or remittance — Caldera extracts gross/net/hours/dates and
            creates a paid invoice + deposit transaction in one step. Inbound email parsing ships
            in v1.5.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-3 text-sm text-muted-foreground">
          <Button onClick={() => setPaystubOpen(true)}>
            <ClipboardPaste className="h-4 w-4" />
            Paste paystub
          </Button>
          <span>
            Tip: works with most ADP/Gusto/QuickBooks remittance formats. The parser flags low
            confidence so you can review before saving.
          </span>
        </CardContent>
      </Card>

      <NewAccountDialog open={accountOpen} onClose={() => setAccountOpen(false)} />
      <NewTransactionDialog open={txnOpen} onClose={() => setTxnOpen(false)} />
      <NewInvoiceDialog open={invoiceOpen} onClose={() => setInvoiceOpen(false)} />
      <MarkPaidDialog
        invoice={payOpen}
        onClose={() => setPayOpen(null)}
      />
      <PaystubDialog open={paystubOpen} onClose={() => setPaystubOpen(false)} />
    </>
  );
}

function Stat({
  label,
  value,
  hint,
  loading,
}: {
  label: string;
  value: string;
  hint?: string;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? <Skeleton className="h-8 w-24" /> : <div className="text-2xl font-semibold">{value}</div>}
        {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}

function NewAccountDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateAccount();
  const [name, setName] = useState("Business Checking");
  const [accountType, setAccountType] = useState<AccountType>("Checking");
  const [purpose, setPurpose] = useState<AccountPurpose>("Operating");
  const [institution, setInstitution] = useState("");
  const [last4, setLast4] = useState("");
  const [openingBalance, setOpeningBalance] = useState("0");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add financial account"
      description="Used to route transactions and the cash allocation plan."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            disabled={create.isPending || !name}
            onClick={async () => {
              await create.mutateAsync({
                name,
                accountType,
                purpose,
                institution: institution || null,
                last4: last4 || null,
                currency: "USD",
                openingBalance: Number(openingBalance) || 0,
                openingBalanceDate: todayIso(),
              });
              onClose();
            }}
          >
            {create.isPending ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Name" className="sm:col-span-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Type">
          <Select value={accountType} onChange={(e) => setAccountType(e.target.value as AccountType)}>
            {(["Checking", "Savings", "CreditCard", "LineOfCredit", "Brokerage", "Retirement", "Other"] as AccountType[]).map(
              (t) => (
                <option key={t} value={t}>{humanAccountType(t)}</option>
              ),
            )}
          </Select>
        </Field>
        <Field label="Purpose">
          <Select value={purpose} onChange={(e) => setPurpose(e.target.value as AccountPurpose)}>
            {(["Operating", "TaxSavings", "Reserves", "OwnerCapital", "Retirement", "Other"] as AccountPurpose[]).map(
              (p) => (
                <option key={p} value={p}>{humanAccountPurpose(p)}</option>
              ),
            )}
          </Select>
        </Field>
        <Field label="Institution">
          <Input value={institution} onChange={(e) => setInstitution(e.target.value)} placeholder="e.g. Summit Credit Union" />
        </Field>
        <Field label="Last 4 digits">
          <Input value={last4} onChange={(e) => setLast4(e.target.value)} maxLength={4} />
        </Field>
        <Field label="Opening balance" className="sm:col-span-2">
          <Input
            type="number"
            inputMode="decimal"
            value={openingBalance}
            onChange={(e) => setOpeningBalance(e.target.value)}
          />
        </Field>
      </div>
    </Dialog>
  );
}

function NewTransactionDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const accounts = useAccounts();
  const create = useCreateTransaction();
  const list = accounts.data ?? [];
  const [accountId, setAccountId] = useState<string>("");
  const [date, setDate] = useState(todayIso());
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("0");
  const [direction, setDirection] = useState<TransactionDirection>("Expense");
  const [scope, setScope] = useState<"Business" | "Personal" | "Mixed">("Business");
  const [businessUsePct, setBusinessUsePct] = useState<string>("80");
  const firstAccount = list[0]?.id;
  const effectiveAccount = accountId || firstAccount || "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add transaction"
      description="Tag against an account; route to the chart of accounts later from the transactions page."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            disabled={create.isPending || !effectiveAccount || !description || Number(amount) <= 0}
            onClick={async () => {
              await create.mutateAsync({
                financialAccountId: effectiveAccount,
                date,
                description,
                direction,
                amount: Number(amount),
                currency: "USD",
                scope,
                businessUsePct:
                  scope === "Mixed" ? (Number(businessUsePct) || 0) / 100 : null,
              });
              onClose();
            }}
          >
            {create.isPending ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Account" className="sm:col-span-2">
          <Select value={effectiveAccount} onChange={(e) => setAccountId(e.target.value)}>
            {list.length === 0 && <option value="">Add an account first</option>}
            {list.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Date">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field label="Direction">
          <Select value={direction} onChange={(e) => setDirection(e.target.value as TransactionDirection)}>
            <option value="Income">Income</option>
            <option value="Expense">Expense</option>
            <option value="Transfer">Transfer</option>
            <option value="OwnerDraw">Owner draw</option>
            <option value="OwnerContribution">Owner contribution</option>
            <option value="Tax">Tax payment</option>
            <option value="Retirement">Retirement contribution</option>
          </Select>
        </Field>
        <Field label="Description" className="sm:col-span-2">
          <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Adobe subscription" />
        </Field>
        <Field label="Amount">
          <Input
            type="number"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Field>
        <Field label="Scope" hint="Business → Schedule C. Personal → Schedule A simulator. Mixed → splits by business-use %.">
          <Select
            value={scope}
            onChange={(e) => setScope(e.target.value as "Business" | "Personal" | "Mixed")}
          >
            <option value="Business">Business (Schedule C)</option>
            <option value="Personal">Personal (Schedule A simulator)</option>
            <option value="Mixed">Mixed (vehicle / cell / internet)</option>
          </Select>
        </Field>
        {scope === "Mixed" && (
          <Field
            label="Business-use %"
            hint="Must be > 50% to qualify for §179 / bonus depreciation on vehicles."
          >
            <Input
              type="number"
              inputMode="decimal"
              min="0"
              max="100"
              value={businessUsePct}
              onChange={(e) => setBusinessUsePct(e.target.value)}
            />
          </Field>
        )}
      </div>
    </Dialog>
  );
}

interface LineItem {
  key: string;
  description: string;
  hours: string;
  rate: string;
  projectTaskId?: string;
}

function NewInvoiceDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateInvoice();
  const assumptions = useStrategyAssumptions();
  const { data: projects = [] } = useBillingProjects();
  const createProject = useCreateBillingProject();
  const createProjectTask = useCreateProjectTask();
  const defaultRate = assumptions.data?.hourlyRate ?? null;

  const [number, setNumber] = useState(`INV-${new Date().getFullYear()}-001`);
  const [issueDate, setIssueDate] = useState(todayIso());
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return d.toISOString().slice(0, 10);
  });
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [newProjectName, setNewProjectName] = useState("");
  const [showNewProject, setShowNewProject] = useState(false);
  const [lines, setLines] = useState<LineItem[]>([
    { key: crypto.randomUUID(), description: "", hours: "", rate: defaultRate != null ? String(defaultRate) : "" },
  ]);
  const [newTaskName, setNewTaskName] = useState("");

  const { data: projectTasks = [] } = useProjectTasks(selectedProjectId || null);

  const total = useMemo(
    () => lines.reduce((s, l) => s + (Number(l.hours) * Number(l.rate) || 0), 0),
    [lines],
  );

  const updateLine = useCallback((key: string, field: keyof LineItem, value: string) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, [field]: value } : l)));
  }, []);

  const addLine = useCallback(() => {
    setLines((prev) => [
      ...prev,
      { key: crypto.randomUUID(), description: "", hours: "", rate: defaultRate != null ? String(defaultRate) : "" },
    ]);
  }, [defaultRate]);

  const removeLine = useCallback((key: string) => {
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((l) => l.key !== key)));
  }, []);

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;
    const p = await createProject.mutateAsync({ name: newProjectName });
    setSelectedProjectId(p.id);
    setNewProjectName("");
    setShowNewProject(false);
  };

  const handleAddTask = async () => {
    if (!newTaskName.trim() || !selectedProjectId) return;
    await createProjectTask.mutateAsync({
      projectId: selectedProjectId,
      name: newTaskName,
      defaultRate: defaultRate ?? undefined,
    });
    setNewTaskName("");
  };

  const handleSelectTask = (lineKey: string, taskId: string) => {
    const task = projectTasks.find((t) => t.id === taskId);
    if (task) {
      setLines((prev) =>
        prev.map((l) =>
          l.key === lineKey
            ? { ...l, description: task.name, rate: task.defaultRate ? String(task.defaultRate) : l.rate, projectTaskId: taskId }
            : l,
        ),
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="New invoice"
      description="Add line items by project. Projects and tasks are remembered for next time."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            disabled={create.isPending || total <= 0 || !number}
            onClick={async () => {
              await create.mutateAsync({
                number,
                issueDate,
                dueDate,
                status: "Sent",
                currency: "USD",
                tax: 0,
                lineItems: lines
                  .filter((l) => Number(l.hours) > 0 && Number(l.rate) > 0)
                  .map((l, i) => ({
                    id: crypto.randomUUID(),
                    invoiceId: "00000000-0000-0000-0000-000000000000",
                    sortOrder: i,
                    description: l.description || "Development services",
                    quantity: Number(l.hours),
                    unitPrice: Number(l.rate),
                    lineTotal: Number(l.hours) * Number(l.rate),
                    lineItemType: "Hours",
                  })),
              });
              onClose();
            }}
          >
            {create.isPending ? "Saving…" : `Issue ${formatMoney(total)}`}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Invoice number" className="sm:col-span-2">
            <Input value={number} onChange={(e) => setNumber(e.target.value)} />
          </Field>
          <Field label="Issue date">
            <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
          </Field>
          <Field label="Due date">
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </Field>
        </div>

        {/* Project selector */}
        <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-2">
          <Field label="Project">
            {showNewProject ? (
              <div className="flex gap-2">
                <Input
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCreateProject()}
                  placeholder="New project name"
                  autoFocus
                  className="flex-1"
                />
                <Button size="sm" onClick={handleCreateProject} disabled={!newProjectName.trim()}>
                  Save
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowNewProject(false)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Select
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  className="flex-1"
                >
                  <option value="">Select project...</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}{p.clientName ? ` (${p.clientName})` : ""}</option>
                  ))}
                </Select>
                <Button size="sm" variant="outline" onClick={() => setShowNewProject(true)}>
                  <Plus className="h-3 w-3" /> New
                </Button>
              </div>
            )}
          </Field>

          {/* Saved tasks for selected project */}
          {selectedProjectId && projectTasks.length > 0 && (
            <div className="text-xs text-muted-foreground">
              Saved tasks: {projectTasks.map((t) => t.name).join(", ")}
            </div>
          )}
        </div>

        {/* Line items */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Line items</span>
            <Button size="sm" variant="outline" onClick={addLine}>
              <Plus className="h-3 w-3" /> Add line
            </Button>
          </div>

          {lines.map((line, idx) => (
            <div key={line.key} className="grid grid-cols-[1fr_80px_80px_24px] gap-2 items-end">
              <div>
                {idx === 0 && <span className="text-xs text-muted-foreground mb-1 block">Task / Description</span>}
                {selectedProjectId && projectTasks.length > 0 ? (
                  <div className="space-y-1">
                    <Select
                      value={line.projectTaskId ?? ""}
                      onChange={(e) => {
                        if (e.target.value === "__new__") {
                          /* handled by the add input below */
                        } else if (e.target.value) {
                          handleSelectTask(line.key, e.target.value);
                        }
                      }}
                    >
                      <option value="">Type or select task...</option>
                      {projectTasks.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </Select>
                    {!line.projectTaskId && (
                      <Input
                        value={line.description}
                        onChange={(e) => updateLine(line.key, "description", e.target.value)}
                        placeholder="Or type a new task name"
                        className="text-xs"
                      />
                    )}
                  </div>
                ) : (
                  <Input
                    value={line.description}
                    onChange={(e) => updateLine(line.key, "description", e.target.value)}
                    placeholder="Description"
                  />
                )}
              </div>
              <div>
                {idx === 0 && <span className="text-xs text-muted-foreground mb-1 block">Hours</span>}
                <Input
                  type="number"
                  inputMode="decimal"
                  value={line.hours}
                  onChange={(e) => updateLine(line.key, "hours", e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                {idx === 0 && <span className="text-xs text-muted-foreground mb-1 block">Rate</span>}
                <Input
                  type="number"
                  inputMode="decimal"
                  value={line.rate}
                  onChange={(e) => updateLine(line.key, "rate", e.target.value)}
                  placeholder="0"
                />
              </div>
              <div className={idx === 0 ? "mt-5" : ""}>
                {lines.length > 1 && (
                  <button
                    onClick={() => removeLine(line.key)}
                    className="text-muted-foreground hover:text-red-500 text-lg leading-none"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Add new task to project inline */}
          {selectedProjectId && (
            <div className="flex gap-2 pt-1">
              <Input
                value={newTaskName}
                onChange={(e) => setNewTaskName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddTask()}
                placeholder="Save a new task to this project..."
                className="flex-1 text-xs"
              />
              <Button size="sm" variant="ghost" onClick={handleAddTask} disabled={!newTaskName.trim()}>
                Save task
              </Button>
            </div>
          )}
        </div>

        <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">
          Total: <strong>{formatMoney(total)}</strong>
        </div>
      </div>
    </Dialog>
  );
}

function MarkPaidDialog({ invoice, onClose }: { invoice: Invoice | null; onClose: () => void }) {
  const accounts = useAccounts();
  const markPaid = useMarkInvoicePaid();
  const list = accounts.data ?? [];
  const operating = list.find((a) => a.purpose === "Operating");
  const [accountId, setAccountId] = useState<string>(operating?.id ?? list[0]?.id ?? "");
  const [paidOn, setPaidOn] = useState(todayIso());
  const [amount, setAmount] = useState("");
  const [payerName, setPayerName] = useState("");

  if (!invoice) return null;

  const effectiveAccount = accountId || operating?.id || list[0]?.id || "";
  const effectiveAmount = amount === "" ? invoice.total : Number(amount);

  return (
    <Dialog
      open={!!invoice}
      onClose={onClose}
      title={`Mark invoice ${invoice.number} paid`}
      description="A deposit transaction will be created in the chosen account, tagged to consulting revenue."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            disabled={markPaid.isPending || !effectiveAccount || effectiveAmount <= 0}
            onClick={async () => {
              await markPaid.mutateAsync({
                invoiceId: invoice.id,
                depositAccountId: effectiveAccount,
                paidOn,
                amount: effectiveAmount,
                payerName: payerName || undefined,
                source: "manual",
              });
              onClose();
            }}
          >
            {markPaid.isPending ? "Saving…" : "Mark paid"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Deposit account" className="sm:col-span-2">
          <Select value={effectiveAccount} onChange={(e) => setAccountId(e.target.value)}>
            {list.length === 0 && <option value="">Add an account first</option>}
            {list.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Paid on">
          <Input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
        </Field>
        <Field label="Amount" hint={`Default: ${formatMoney(invoice.total)}`}>
          <Input
            type="number"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={String(invoice.total)}
          />
        </Field>
        <Field label="Payer (optional)" className="sm:col-span-2">
          <Input value={payerName} onChange={(e) => setPayerName(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

function invoiceTone(s: Invoice["status"]) {
  switch (s) {
    case "Paid":
      return "success" as const;
    case "Sent":
      return "info" as const;
    case "Overdue":
      return "danger" as const;
    case "PartiallyPaid":
      return "warning" as const;
    default:
      return "neutral" as const;
  }
}

function humanAccountType(t: AccountType) {
  switch (t) {
    case "CreditCard":
      return "Credit card";
    case "LineOfCredit":
      return "Line of credit";
    default:
      return t;
  }
}
function humanAccountPurpose(p: AccountPurpose) {
  switch (p) {
    case "TaxSavings":
      return "Tax savings";
    case "OwnerCapital":
      return "Owner capital";
    default:
      return p;
  }
}

function PaystubDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const accounts = useAccounts();
  const parse = useParsePaystub();
  const ingest = useIngestPaystub();

  const list = accounts.data ?? [];
  const operating = list.find((a) => a.purpose === "Operating") ?? list[0];

  const [raw, setRaw] = useState("");
  const [parsed, setParsed] = useState<ParsedPaystub | null>(null);
  const [accountId, setAccountId] = useState<string>(operating?.id ?? "");
  const [gross, setGross] = useState("");
  const [net, setNet] = useState("");
  const [hours, setHours] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [payDate, setPayDate] = useState("");
  const [payer, setPayer] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [savedInvoice, setSavedInvoice] = useState<string | null>(null);

  const reset = () => {
    setRaw("");
    setParsed(null);
    setGross("");
    setNet("");
    setHours("");
    setPeriodStart("");
    setPeriodEnd("");
    setPayDate("");
    setPayer("");
    setConfirmation("");
    setSavedInvoice(null);
  };

  const onParse = async () => {
    const result = await parse.mutateAsync(raw);
    setParsed(result);
    setGross(result.grossPay?.toString() ?? "");
    setNet(result.netPay?.toString() ?? "");
    setHours(result.hours?.toString() ?? "");
    setPeriodStart(result.periodStart ?? "");
    setPeriodEnd(result.periodEnd ?? "");
    setPayDate(result.payDate ?? "");
    setPayer(result.payer ?? "");
    setConfirmation(result.checkOrConfirmationNumber ?? "");
  };

  const onIngest = async () => {
    if (!accountId) return;
    const result = await ingest.mutateAsync({
      depositAccountId: accountId,
      grossAmount: Number(gross),
      netAmount: net ? Number(net) : undefined,
      hours: hours ? Number(hours) : undefined,
      periodStart: periodStart || undefined,
      periodEnd: periodEnd || undefined,
      payDate: payDate || undefined,
      payer: payer || undefined,
      checkOrConfirmationNumber: confirmation || undefined,
    });
    setSavedInvoice(result.invoiceNumber);
  };

  const grossNum = Number(gross) || 0;
  const canIngest = !!accountId && grossNum > 0 && !ingest.isPending;

  return (
    <Dialog
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Ingest a paystub"
      description="Paste the email body or PDF text. Caldera extracts the numbers; you confirm before saving."
      size="lg"
      footer={
        savedInvoice ? (
          <Button onClick={() => { reset(); onClose(); }}>Done</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            {!parsed ? (
              <Button onClick={onParse} disabled={!raw || parse.isPending}>
                {parse.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Parse
              </Button>
            ) : (
              <Button onClick={onIngest} disabled={!canIngest}>
                {ingest.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Save invoice + deposit
              </Button>
            )}
          </>
        )
      }
    >
      {savedInvoice ? (
        <div className="space-y-2 py-3 text-sm">
          <div className="flex items-center gap-2 text-emerald-300">
            <Sparkles className="h-4 w-4" />
            Saved invoice <strong>{savedInvoice}</strong> + deposit transaction.
          </div>
          <p className="text-muted-foreground">
            Refresh the invoices and transactions tables to see the new entries.
          </p>
        </div>
      ) : !parsed ? (
        <div className="space-y-3">
          <Field label="Paste paystub email or PDF text">
            <textarea
              className="min-h-[200px] w-full rounded-md border border-border bg-background p-3 text-sm font-mono shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              placeholder={"Pay Period: 04/01/2026 to 04/14/2026\nPay Date: 04/15/2026\nGross Pay: 7,200.00\nNet Pay: 5,640.00\nTotal Hours: 60\nHourly Rate: 120.00"}
            />
          </Field>
          {parse.isError && (
            <p className="text-xs text-destructive">Could not parse. Try again or fill manually.</p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
            Confidence: <strong>{Math.round((parsed.confidence ?? 0) * 100)}%</strong>
            {parsed.warnings.length > 0 && (
              <ul className="mt-2 space-y-1 text-amber-300">
                {parsed.warnings.map((w, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                    {w}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <Field label="Deposit account">
            <Select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              {list.length === 0 && <option value="">Add an account first</option>}
              {list.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>
          </Field>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Gross amount">
              <Input
                type="number"
                inputMode="decimal"
                value={gross}
                onChange={(e) => setGross(e.target.value)}
              />
            </Field>
            <Field label="Net amount (deposit)">
              <Input
                type="number"
                inputMode="decimal"
                value={net}
                onChange={(e) => setNet(e.target.value)}
                placeholder={gross}
              />
            </Field>
            <Field label="Hours">
              <Input
                type="number"
                inputMode="decimal"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
              />
            </Field>
            <Field label="Pay date">
              <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
            </Field>
            <Field label="Period start">
              <Input
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
              />
            </Field>
            <Field label="Period end">
              <Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
            </Field>
            <Field label="Payer (client name)" className="sm:col-span-2">
              <Input value={payer} onChange={(e) => setPayer(e.target.value)} />
            </Field>
            <Field label="Confirmation / trace number" className="sm:col-span-2">
              <Input
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                placeholder="ACH trace, check #, or remittance ID"
              />
            </Field>
          </div>
        </div>
      )}
    </Dialog>
  );
}

// =====================================================================
// Phase C: auto-classification suggestions banner
// =====================================================================
function SuggestionsCard() {
  const sug = useTransactionSuggestions();
  const txns = useTransactions();
  const apply = useApplyClassification();

  const items = sug.data ?? [];
  if (sug.isLoading) {
    return null; // quiet on first load
  }
  if (items.length === 0) {
    return null;
  }

  const txnById = new Map((txns.data ?? []).map((t) => [t.id, t]));

  return (
    <Card className="mt-8 border-sky-500/30 bg-sky-500/[0.06]">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-sky-300" /> Suggested classifications ({items.length})
        </CardTitle>
        <CardDescription>
          Auto-suggested categories from counterparty matching. Review and apply — nothing is written silently.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-2">Date</th>
              <th className="py-2">Description</th>
              <th className="py-2">Amount</th>
              <th className="py-2">Suggestion</th>
              <th className="py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {items.slice(0, 25).map((row) => {
              const t = txnById.get(row.transactionId);
              if (!t) return null;
              const s = row.suggestion;
              const pct = s.suggestedBusinessUsePct;
              return (
                <tr key={row.transactionId} className="border-b border-border/50 align-top">
                  <td className="py-2 text-muted-foreground">{formatDate(t.date)}</td>
                  <td className="py-2">
                    <div className="font-medium">{t.description}</div>
                    {t.counterParty && (
                      <div className="text-xs text-muted-foreground">{t.counterParty}</div>
                    )}
                  </td>
                  <td className="py-2 tabular-nums">{formatMoney(t.amount)}</td>
                  <td className="py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone={s.suggestedScope === "Business" ? "info" : s.suggestedScope === "Personal" ? "warning" : "success"}>
                        {s.suggestedScope}
                      </Badge>
                      <span className="font-medium">{s.suggestedChartOfAccountName}</span>
                      <span className="text-xs text-muted-foreground">({s.suggestedChartOfAccountCode})</span>
                      {pct != null && (
                        <Badge tone="neutral">{Math.round(pct * 100)}% business</Badge>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">{s.reason}</p>
                  </td>
                  <td className="py-2 text-right">
                    <Button
                      size="sm"
                      onClick={() =>
                        apply.mutate({
                          id: t.id,
                          chartOfAccountId: s.suggestedChartOfAccountId,
                          scope: s.suggestedScope,
                          businessUsePct: pct,
                        })
                      }
                    >
                      Apply
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

